import { useMemo, useRef, useState } from 'react';
import { createFileRoute, stripSearchParams } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { z } from 'zod';
import { type ColumnFiltersState, type OnChangeFn, type PaginationState, type SortingState } from '@tanstack/react-table';
import { CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';

import { useCellConversations, useToggleStarred, useCellConversationStatistics } from '@/hooks/use-conversations';
import { useCellScope } from '@/hooks/use-cell-scope';
import { CellSelect } from '@/components/cells/cell-select';
import { CellScopeAlerts } from '@/components/cells/cell-scope-alerts';
import { CELLS, findCell } from '@/lib/cells/cells';
import { ALL_CELLS_LIST_LIMIT } from '@/lib/cells/cell-conversations';
import { homeCellExclusions } from '@/lib/cells/cell-statistics';
import { useExportConversations } from '@/hooks/use-export-conversations';
import { createConversationColumns } from '@/components/conversations/columns';
import { ResizableConversationPreview } from '@/components/conversations/resizable-conversation-preview';
import { ExportOptionsDialog, type ExportOptions } from '@/components/conversations/export-options-dialog';
import { DataTable, DataTableToolbar, useDataTable, type DataTableToolbarRef } from '@/components/common/data-table';
import { Page, PageContent, PageHeader, PageTitle } from '@/components/common/page';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

const defaultValues = {
  sortBy: 'conversation_date' as const,
  sortDirection: 'desc' as 'desc' | 'asc',
  page: 0,
  pageSize: 50,
  q: '',
  source_identifier: '',
  category: [],
  ai_category: [],
  workflow: [],
  sentiment: [],
  interest_level: [],
  resolution: [],
  source: [],
  starred: false,
  tenant_id: [],
  minTurns: 1,
  maxTurns: 11,
  preview: '',
  previewTenantId: '',
  dateRange: 'all' as const,
  dateFrom: '',
  dateTo: '',
  cell: 'all',
};

const searchSchema = z.object({
  sortBy: z.enum(['conversation_date', 'last_turn_at', 'tenant_id', 'category', 'turn_count', 'source']).default(defaultValues.sortBy),
  sortDirection: z.enum(['desc', 'asc']).default(defaultValues.sortDirection),
  page: z.number().min(0).default(defaultValues.page),
  pageSize: z.number().min(10).max(200).default(defaultValues.pageSize),
  q: z.string().default(defaultValues.q),
  source_identifier: z.string().default(defaultValues.source_identifier),
  category: z.array(z.string()).default(defaultValues.category),
  ai_category: z.array(z.string()).default(defaultValues.ai_category),
  workflow: z.array(z.string()).default(defaultValues.workflow),
  sentiment: z.array(z.string()).default(defaultValues.sentiment),
  interest_level: z.array(z.string()).default(defaultValues.interest_level),
  resolution: z.array(z.string()).default(defaultValues.resolution),
  source: z.array(z.string()).default(defaultValues.source),
  starred: z.boolean().default(defaultValues.starred),
  tenant_id: z.array(z.string()).default(defaultValues.tenant_id),
  minTurns: z.number().min(0).default(defaultValues.minTurns),
  maxTurns: z.number().min(0).default(defaultValues.maxTurns),
  preview: z.string().default(defaultValues.preview),
  previewTenantId: z.string().default(defaultValues.previewTenantId),
  dateRange: z.enum(['day', 'week', 'month', 'quarter', 'all', 'custom']).default(defaultValues.dateRange),
  dateFrom: z.string().default(defaultValues.dateFrom),
  dateTo: z.string().default(defaultValues.dateTo),
  cell: z.string().default(defaultValues.cell),
});

type SearchSchema = z.infer<typeof searchSchema>;

export const Route = createFileRoute('/agent/conversations/')({
  component: RouteComponent,
  validateSearch: zodValidator(searchSchema),
  search: { middlewares: [stripSearchParams(defaultValues)] },
});

function RouteComponent() {
  const searchParams = Route.useSearch();
  const navigate = Route.useNavigate();
  const toolbarRef = useRef<DataTableToolbarRef | null>(null);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false);

  const tenantFilterId = searchParams.tenant_id.length > 0 ? searchParams.tenant_id[0] : undefined;
  const cellFilter = CELLS.length > 1 ? findCell(searchParams.cell)?.id ?? 'all' : 'all';
  const scope = useCellScope({ cellFilter, tenantId: tenantFilterId });
  const { tenants, queriedCells } = scope;
  // The tenant filter offers the chosen cell's tenants.
  const filterTenants = useMemo(
    () =>
      cellFilter === 'all'
        ? tenants
        : tenants?.filter((t) => t.deployment_cell === cellFilter || t.id === tenantFilterId),
    [tenants, cellFilter, tenantFilterId],
  );

  const { date_from, date_to } = useMemo(() => {
    if (searchParams.dateRange === 'all') {
      return { date_from: undefined, date_to: undefined };
    }
    if (searchParams.dateRange === 'custom') {
      let dateFromIso: string | undefined;
      let dateToIso: string | undefined;
      if (searchParams.dateFrom) {
        const from = new Date(searchParams.dateFrom);
        dateFromIso = new Date(
          from.getFullYear(), from.getMonth(), from.getDate()
        ).toISOString();
      }
      if (searchParams.dateTo) {
        const to = new Date(searchParams.dateTo);
        dateToIso = new Date(
          to.getFullYear(), to.getMonth(), to.getDate(), 23, 59, 59
        ).toISOString();
      }
      return {
        date_from: dateFromIso,
        date_to: dateToIso,
      };
    }
    const now = new Date();
    let fromDate: Date;
    switch (searchParams.dateRange) {
      case 'day':
        fromDate = new Date(
          now.getFullYear(), now.getMonth(), now.getDate() - 1
        );
        break;
      case 'week':
        fromDate = new Date(
          now.getFullYear(), now.getMonth(), now.getDate() - 7
        );
        break;
      case 'month':
        fromDate = new Date(
          now.getFullYear(), now.getMonth(), now.getDate() - 30
        );
        break;
      case 'quarter':
        fromDate = new Date(
          now.getFullYear(), now.getMonth(), now.getDate() - 90
        );
        break;
      default:
        return { date_from: undefined, date_to: undefined };
    }
    const toDate = new Date(
      now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59
    );
    return {
      date_from: fromDate.toISOString(),
      date_to: toDate.toISOString(),
    };
  }, [searchParams.dateRange, searchParams.dateFrom, searchParams.dateTo]);

  // Fetch statistics without tenant filter so workflow options are always available
  const { stats: statistics } = useCellConversationStatistics(
    queriedCells,
    { excludeTenantIds: (cell) => (scope.showCells ? homeCellExclusions(cell.id, tenants ?? [], scope.excludeTenantIds(cell)) : scope.excludeTenantIds(cell)) },
  );

  const conversations = useCellConversations(queriedCells, {
    page: searchParams.page + 1, // API uses 1-based pagination
    page_size: searchParams.pageSize,
    search: searchParams.q || undefined,
    source_identifier: searchParams.source_identifier || undefined,
    category: searchParams.category.length > 0 ? searchParams.category[0] : undefined,
    ai_category: searchParams.ai_category.length > 0 ? searchParams.ai_category[0] : undefined,
    workflow_type: searchParams.workflow.length > 0 ? searchParams.workflow[0] : undefined,
    sentiment: searchParams.sentiment.length > 0 ? searchParams.sentiment[0] : undefined,
    interest_level: searchParams.interest_level.length > 0 ? searchParams.interest_level[0] : undefined,
    resolution: searchParams.resolution.length > 0 ? searchParams.resolution[0] : undefined,
    source: searchParams.source.length > 0 ? searchParams.source[0] : undefined,
    is_starred: searchParams.starred || undefined,
    tenant_id: searchParams.tenant_id.length > 0 ? searchParams.tenant_id[0] : undefined,
    min_turns: searchParams.minTurns,
    max_turns: searchParams.maxTurns < 11 ? searchParams.maxTurns : undefined,
    sortBy: searchParams.sortBy,
    sortDirection: searchParams.sortDirection,
    date_from,
    date_to,
  }, scope.excludeTenantIds);

  const toggleStarMutation = useToggleStarred();
  const exportConversationsMutation = useExportConversations();

  const conversationsData = conversations.conversations;
  const totalCount = conversations.totalCount;
  const pageCount = conversations.pageCount;
  const listLoading =
    conversations.isLoading || (queriedCells.length === 0 && !scope.tenantCellUnknown);

  const sortingState: SortingState = [{ id: searchParams.sortBy, desc: searchParams.sortDirection === 'desc' }];
  const paginationState: PaginationState = { pageSize: searchParams.pageSize, pageIndex: searchParams.page };

  const columnFiltersState: ColumnFiltersState = [
    ...(searchParams.category.length > 0 ? [{ id: 'category', value: searchParams.category }] : []),
    ...(searchParams.ai_category.length > 0 ? [{ id: 'ai_category', value: searchParams.ai_category }] : []),
    ...(searchParams.workflow.length > 0 ? [{ id: 'workflow_types', value: searchParams.workflow }] : []),
    ...(searchParams.sentiment.length > 0 ? [{ id: 'sentiment', value: searchParams.sentiment }] : []),
    ...(searchParams.interest_level.length > 0 ? [{ id: 'interest_level', value: searchParams.interest_level }] : []),
    ...(searchParams.resolution.length > 0 ? [{ id: 'resolution', value: searchParams.resolution }] : []),
    ...(searchParams.source.length > 0 ? [{ id: 'source', value: searchParams.source }] : []),
    ...(searchParams.starred ? [{ id: 'is_starred', value: true }] : []),
    ...(searchParams.tenant_id.length > 0 ? [{ id: 'tenant_id', value: searchParams.tenant_id }] : []),
  ];

  const handleToggleStar = async (conversationId: string, tenantId: string) => {
    await toggleStarMutation.mutateAsync({ conversationId, tenantId });
  };

  const handleExport = () => {
    if (scope.showCells && queriedCells.length === 0) {
      toast.info('Tenants are still loading', { description: 'Export once the list has loaded.' });
      return;
    }
    setExportDialogOpen(true);
  };

  const handleExportConfirm = (options: ExportOptions) => {
    setExportDialogOpen(false);

    exportConversationsMutation.mutate({
      filters: {
        search: searchParams.q || undefined,
        source_identifier: searchParams.source_identifier || undefined,
        category: searchParams.category.length > 0 ? searchParams.category[0] : undefined,
        ai_category: searchParams.ai_category.length > 0 ? searchParams.ai_category[0] : undefined,
        workflow_type: searchParams.workflow.length > 0 ? searchParams.workflow[0] : undefined,
        sentiment: searchParams.sentiment.length > 0 ? searchParams.sentiment[0] : undefined,
        interest_level: searchParams.interest_level.length > 0 ? searchParams.interest_level[0] : undefined,
        resolution: searchParams.resolution.length > 0 ? searchParams.resolution[0] : undefined,
        source: searchParams.source.length > 0 ? searchParams.source[0] : undefined,
        is_starred: searchParams.starred || undefined,
        tenant_id: searchParams.tenant_id.length > 0 ? searchParams.tenant_id[0] : undefined,
        min_turns: searchParams.minTurns,
        max_turns: searchParams.maxTurns < 11 ? searchParams.maxTurns : undefined,
        sortBy: searchParams.sortBy,
        sortDirection: searchParams.sortDirection,
        date_from,
        date_to,
      },
      cells: {
        cells: queriedCells,
        // Each tenant only from its own cell; internal tenants stay in the export as before.
        excludeTenantIds: (cell) =>
          scope.showCells && !tenantFilterId ? homeCellExclusions(cell.id, tenants ?? [], []) : [],
        tenants,
      },
      options: {
        includeConversationContent: options.includeConversationContent,
        includeRawData: options.includeRawData,
      },
    });
  };

  const updateSearch = (updates: Partial<SearchSchema>) => {
    void navigate({ to: '.', search: (prev) => ({ ...prev, ...updates }) });
  };

  const onSortingChange: OnChangeFn<SortingState> = (value) => {
    const newState = typeof value === 'function' ? value(sortingState) : value;
    if (newState.length > 0) {
      updateSearch({
        sortBy: newState[0].id as SearchSchema['sortBy'],
        sortDirection: newState[0].desc ? 'desc' : 'asc',
      });
    }
  };

  const onGlobalFilterChange: OnChangeFn<string> = (value) => {
    const newValue = typeof value === 'function' ? value(searchParams.q) : value;
    updateSearch({ q: newValue, page: 0 });
  };

  const onColumnFiltersChange: OnChangeFn<ColumnFiltersState> = (value) => {
    const newState = typeof value === 'function' ? value(columnFiltersState) : value;

    const newSearch: Partial<SearchSchema> = { page: 0 };

    if (newState.length === 0) {
      newSearch.category = [];
      newSearch.ai_category = [];
      newSearch.workflow = [];
      newSearch.sentiment = [];
      newSearch.interest_level = [];
      newSearch.resolution = [];
      newSearch.source = [];
      newSearch.starred = false;
      newSearch.tenant_id = [];
    } else {
      const filterMap = Object.fromEntries(
        newState.map((filter) => [filter.id, filter.value]),
      );

      newSearch.category = filterMap.category
        ? (filterMap.category as string[]) : [];
      newSearch.ai_category = filterMap.ai_category
        ? (filterMap.ai_category as string[]) : [];
      newSearch.workflow = filterMap.workflow_types
        ? (filterMap.workflow_types as string[]) : [];
      newSearch.sentiment = filterMap.sentiment
        ? (filterMap.sentiment as string[]) : [];
      newSearch.interest_level = filterMap.interest_level
        ? (filterMap.interest_level as string[]) : [];
      newSearch.resolution = filterMap.resolution
        ? (filterMap.resolution as string[]) : [];
      newSearch.source = filterMap.source
        ? (filterMap.source as string[]) : [];
      newSearch.starred = filterMap.is_starred
        ? (filterMap.is_starred as boolean) : false;
      newSearch.tenant_id = filterMap.tenant_id
        ? (filterMap.tenant_id as string[]) : [];
    }

    updateSearch(newSearch);
  };

  const onPaginationChange: OnChangeFn<PaginationState> = (value) => {
    const newState = typeof value === 'function' ? value(paginationState) : value;
    updateSearch({ page: newState.pageIndex, pageSize: newState.pageSize });
  };

  const conversationColumns = createConversationColumns(tenants, handleToggleStar);

  const handleCellChange = (value: string) => {
    const keepTenant = scope.selectedTenant?.deployment_cell === value;
    updateSearch({ cell: value, page: 0, ...(keepTenant ? {} : { tenant_id: [] }) });
  };

  const table = useDataTable({
    columns: conversationColumns,
    name: 'conversations',
    data: conversationsData,
    meta: { tenants: filterTenants, statistics },
    pageCount: pageCount,
    getRowId: (row) => row.id,
    initialState: {
      columnVisibility: {
        source_identifier: false, // Hidden by default
        workflow_types: false,    // Hidden by default
      },
    },
    state: {
      sorting: sortingState,
      pagination: paginationState,
      columnFilters: columnFiltersState,
      globalFilter: searchParams.q,
    },
    onSortingChange,
    onPaginationChange,
    onColumnFiltersChange,
    onGlobalFilterChange,
    enableRowSelection: false,
  });

  return (
    <Page>
      <PageHeader>
        <PageTitle>Conversations</PageTitle>
      </PageHeader>
      <div className="space-y-3">
        <DataTableToolbar
          ref={toolbarRef}
          table={table}
          onRefresh={() => { conversations.refetch(); }}
          onExport={handleExport}
          isLoading={conversations.isRefetching}
          isExporting={exportConversationsMutation.isPending}
        >
          <Input
            placeholder="Source ID (e.g., slack:T01:...)"
            value={searchParams.source_identifier}
            onChange={(e) => { updateSearch({ source_identifier: e.target.value, page: 0 }); }}
            className="h-9 w-64"
          />
          {searchParams.source_identifier && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                updateSearch({ source_identifier: '', page: 0 });
              }}
              className="h-9 px-2"
            >
              Clear Source ID
            </Button>
          )}
        </DataTableToolbar>
        <div className="flex items-center gap-4 flex-wrap">
          <CellSelect
            value={scope.selectedTenant?.deployment_cell ?? cellFilter}
            onChange={handleCellChange}
            className="h-9"
          />
          <div className="flex items-center gap-2">
            <CalendarIcon className="h-4 w-4 text-muted-foreground" />
            {searchParams.dateRange === 'custom' ? (
              <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-[260px] justify-start text-left font-normal h-9",
                      !searchParams.dateFrom && !searchParams.dateTo && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {searchParams.dateFrom && searchParams.dateTo ? (
                      `${format(new Date(searchParams.dateFrom), "MMM d, yyyy")} - ${format(new Date(searchParams.dateTo), "MMM d, yyyy")}`
                    ) : searchParams.dateFrom ? (
                      `From ${format(new Date(searchParams.dateFrom), "MMM d, yyyy")}`
                    ) : searchParams.dateTo ? (
                      `Until ${format(new Date(searchParams.dateTo), "MMM d, yyyy")}`
                    ) : (
                      <span>Pick a date range</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <div className="flex">
                    <div className="flex flex-col gap-1 border-r p-3 min-w-[140px]">
                      <p className="text-xs text-muted-foreground font-medium mb-1 px-2">Presets</p>
                      <Button variant="ghost" size="sm" className="justify-start" onClick={() => { updateSearch({ dateRange: 'day', dateFrom: '', dateTo: '', page: 0 }); setDatePickerOpen(false); }}>Last 24 hours</Button>
                      <Button variant="ghost" size="sm" className="justify-start" onClick={() => { updateSearch({ dateRange: 'week', dateFrom: '', dateTo: '', page: 0 }); setDatePickerOpen(false); }}>Last 7 days</Button>
                      <Button variant="ghost" size="sm" className="justify-start" onClick={() => { updateSearch({ dateRange: 'month', dateFrom: '', dateTo: '', page: 0 }); setDatePickerOpen(false); }}>Last 30 days</Button>
                      <Button variant="ghost" size="sm" className="justify-start" onClick={() => { updateSearch({ dateRange: 'quarter', dateFrom: '', dateTo: '', page: 0 }); setDatePickerOpen(false); }}>Last 90 days</Button>
                      <Button variant="ghost" size="sm" className="justify-start" onClick={() => { updateSearch({ dateRange: 'all', dateFrom: '', dateTo: '', page: 0 }); setDatePickerOpen(false); }}>All time</Button>
                    </div>
                    <div className="flex flex-col">
                      <Calendar
                        mode="range"
                        selected={{
                          from: searchParams.dateFrom ? new Date(searchParams.dateFrom) : undefined,
                          to: searchParams.dateTo ? new Date(searchParams.dateTo) : undefined,
                        }}
                        onSelect={(range) => {
                          const fromStr = range?.from
                            ? new Date(range.from.getFullYear(), range.from.getMonth(), range.from.getDate()).toISOString()
                            : '';
                          const toStr = range?.to
                            ? new Date(range.to.getFullYear(), range.to.getMonth(), range.to.getDate()).toISOString()
                            : '';
                          updateSearch({ dateFrom: fromStr, dateTo: toStr, page: 0 });
                        }}
                        numberOfMonths={2}
                      />
                      <div className="flex items-center justify-between border-t p-3">
                        <Button variant="ghost" size="sm" onClick={() => { updateSearch({ dateFrom: '', dateTo: '' }); }}>Clear</Button>
                        <Button size="sm" onClick={() => { setDatePickerOpen(false); }} disabled={!searchParams.dateFrom && !searchParams.dateTo}>Apply</Button>
                      </div>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            ) : (
              <Select
                value={searchParams.dateRange}
                onValueChange={(value) => {
                  if (value === 'custom') {
                    updateSearch({ dateRange: 'custom', dateFrom: '', dateTo: '', page: 0 });
                    setDatePickerOpen(true);
                  } else {
                    updateSearch({ dateRange: value as typeof searchParams.dateRange, dateFrom: '', dateTo: '', page: 0 });
                  }
                }}
              >
                <SelectTrigger className="w-[160px] h-9">
                  <SelectValue placeholder="Date range" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="day">Last 24 hours</SelectItem>
                  <SelectItem value="week">Last 7 days</SelectItem>
                  <SelectItem value="month">Last 30 days</SelectItem>
                  <SelectItem value="quarter">Last 90 days</SelectItem>
                  <SelectItem value="all">All time</SelectItem>
                  <SelectItem value="custom">Custom range...</SelectItem>
                </SelectContent>
              </Select>
            )}
          </div>
          <div className="flex items-center gap-4 flex-1">
            <Label className="text-sm font-medium whitespace-nowrap">Messages:</Label>
            <div className="flex items-center gap-3 flex-1 max-w-xs">
              <span className="text-xs text-muted-foreground w-12 text-right">
                {searchParams.minTurns === 0 ? 'Any' : searchParams.minTurns}
              </span>
              <Slider
                min={0}
                max={11}
                step={1}
                value={[searchParams.minTurns, searchParams.maxTurns]}
                onValueChange={(value) => {
                  updateSearch({ minTurns: value[0], maxTurns: value[1], page: 0 });
                }}
                className="flex-1"
              />
              <span className="text-xs text-muted-foreground w-12">
                {searchParams.maxTurns === 11 ? '10+' : searchParams.maxTurns === 0 ? 'Any' : searchParams.maxTurns}
              </span>
            </div>
            {(searchParams.minTurns !== 1 || searchParams.maxTurns < 11) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  updateSearch({ minTurns: 1, maxTurns: 11, page: 0 });
                }}
                className="h-7 text-xs"
              >
                Reset
              </Button>
            )}
          </div>
        </div>
      </div>
      {scope.showCells && (
        <div className="flex flex-col gap-2">
          <CellScopeAlerts
            scope={scope}
            failures={conversations.failures}
            loadedCells={conversations.perCell.map(({ cell }) => cell)}
            isLoading={listLoading}
          />
          {!listLoading && !scope.tenantCellUnknown && (
            <p className="text-muted-foreground text-xs" data-testid="cell-split">
              {[
                ...conversations.perCell.map(({ cell, total }) => `${cell.residency} ${total}`),
                ...conversations.failures.map(({ cell }) => `${cell.residency} did not load`),
              ].join(' | ')}
              {conversations.capped &&
                ` - All cells pages through the first ${ALL_CELLS_LIST_LIMIT}; pick a cell to see the rest.`}
            </p>
          )}
        </div>
      )}
      <PageContent className="flex items-start">
        <DataTable
          table={table}
          totalCount={totalCount}
          className={searchParams.preview !== '' ? 'flex-1' : 'w-full'}
          selectedRowId={searchParams.preview}
          scope="conversations"
          data={conversationsData}
          getRowUrl={(row) => `?preview=${row.id}&previewTenantId=${row.tenant_id}`}
          isLoading={listLoading || conversations.isRefetching}
          toolbarRef={toolbarRef}
        />
        {searchParams.preview !== '' && (
          <ResizableConversationPreview
            conversationId={searchParams.preview}
            tenantId={searchParams.previewTenantId}
            onPreviewClose={() => {
              void navigate({ to: '.', search: (prev) => ({ ...prev, preview: '', previewTenantId: '' }) });
            }}
          />
        )}
      </PageContent>

      <ExportOptionsDialog
        open={exportDialogOpen}
        onOpenChange={setExportDialogOpen}
        onConfirm={handleExportConfirm}
        isExporting={exportConversationsMutation.isPending}
      />
    </Page>
  );
}
